import { useEffect, useState } from 'react';
import { useLocale } from '../../context';
import { api } from '../../api';
import { getDeviceId } from '../../utils/device';
import {
  getPlatformAssertion,
  shouldShowWebAuthnLogin,
  mapWebAuthnError,
  markLocalPasskeyHint,
} from '../../utils/webauthn';

function IconFaceId() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3.5" y="3.5" width="17" height="17" rx="4" stroke="currentColor" strokeWidth="1.6" />
      <path d="M8.5 10.2v1.2M15.5 10.2v1.2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M9.2 15.2c.9.9 1.8 1.3 2.8 1.3s1.9-.4 2.8-1.3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M12 8.8v3.2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function isNeedRegisterMessage(message) {
  const msg = String(message || '').toLowerCase();
  return /kayıtlı biyometrik|kayitli biyometrik|no passkey|not registered|credential|biyometrik giriş yok|biyometrik giris yok/.test(msg);
}

export default function WebAuthnLoginButton({ onSuccess, disabled }) {
  const _locale = useLocale();
  const tr = _locale.t;
  const [supported, setSupported] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    shouldShowWebAuthnLogin()
      .then((ok) => { if (!cancelled) setSupported(ok); })
      .catch(() => { if (!cancelled) setSupported(false); });
    return () => { cancelled = true; };
  }, []);

  if (!supported) return null;

  const handleClick = async () => {
    setError('');
    setLoading(true);
    try {
      const options = await api.webauthnLoginOptions();
      const { challengeId, ...publicKey } = options;
      const assertion = await getPlatformAssertion(publicKey);
      if (!assertion) throw new Error(tr('auth.webauthn.failed'));
      const res = await api.webauthnLoginVerify({
        challengeId,
        response: assertion,
        deviceId: getDeviceId(),
      });
      const token = res?.token;
      const user = res?.user;
      if (!token || !user?.id) throw new Error(tr('auth.webauthn.failed'));
      markLocalPasskeyHint();
      await new Promise((resolve) => window.setTimeout(resolve, 50));
      onSuccess?.(token, user);
    } catch (err) {
      const code = mapWebAuthnError(err);
      if (code === 'cancelled') {
        setError('');
      } else if (code === 'unsupported') {
        setError(tr('auth.webauthn.unsupported'));
      } else if (code === 'no_credentials' || isNeedRegisterMessage(err?.message)) {
        setError(tr('auth.webauthn.needRegister'));
      } else {
        setError(err.message || tr('auth.webauthn.failed'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-webauthn">
      <button
        type="button"
        className="auth-webauthn__button"
        onClick={handleClick}
        disabled={disabled || loading}
      >
        <span className="auth-webauthn__icon"><IconFaceId /></span>
        <span>{loading ? '...' : tr('auth.webauthn.login')}</span>
      </button>
      {error ? <div className="auth-error">{error}</div> : null}
    </div>
  );
}
